import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Users,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  UserCheck,
  UserX
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function AdminEmployees() {
  const { authAxios } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'employee',
    department: '',
    phone: '',
    hourly_rate: 0,
    is_truck_driver: false
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await authAxios.get('/employees');
      setEmployees(res.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'employee',
      department: '',
      phone: '',
      hourly_rate: 0,
      is_truck_driver: false
    });
    setEditingEmployee(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      email: employee.email,
      password: '',
      full_name: employee.full_name,
      role: employee.role,
      department: employee.department || '',
      phone: employee.phone || '',
      hourly_rate: employee.hourly_rate || 0,
      is_truck_driver: !!employee.is_truck_driver
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingEmployee) {
        await authAxios.put(`/employees/${editingEmployee.id}`, {
          email: formData.email,
          full_name: formData.full_name,
          department: formData.department,
          phone: formData.phone,
          hourly_rate: parseFloat(formData.hourly_rate) || 0,
          is_truck_driver: formData.is_truck_driver
        });
        toast.success('Employee updated successfully!');
      } else {
        await authAxios.post('/employees', {
          ...formData,
          hourly_rate: parseFloat(formData.hourly_rate) || 0
        });
        toast.success('Employee created successfully!');
      }
      setDialogOpen(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (employee) => {
    try {
      await authAxios.put(`/employees/${employee.id}`, {
        is_active: !employee.is_active
      });
      toast.success(`Employee ${employee.is_active ? 'deactivated' : 'activated'}!`);
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to update employee status');
    }
  };

  const handleDelete = async (employee) => {
    if (!window.confirm(`Are you sure you want to delete ${employee.full_name}? This will also delete all their time records.`)) {
      return;
    }
    try {
      await authAxios.delete(`/employees/${employee.id}`);
      toast.success('Employee deleted successfully!');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const filteredEmployees = employees.filter(emp => 
    emp.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-employees">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Employees</h1>
          <p className="text-stone-500 mt-1">Manage your team members</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={openCreateDialog}
              className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/20"
              data-testid="add-employee-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  data-testid="employee-name-input"
                  className="bg-stone-50 border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="employee-email-input"
                  className="bg-stone-50 border-stone-200"
                />
              </div>
              {!editingEmployee && (
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="employee-password-input"
                    className="bg-stone-50 border-stone-200"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="employee-role-select" className="bg-stone-50 border-stone-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  data-testid="employee-department-input"
                  className="bg-stone-50 border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="employee-phone-input"
                  className="bg-stone-50 border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
                <Input
                  id="hourly_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                  data-testid="employee-rate-input"
                  className="bg-stone-50 border-stone-200"
                  placeholder="25.00"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <Label>Truck driver</Label>
                  <p className="text-xs text-stone-500">Can clock in anywhere, no job site needed</p>
                </div>
                <Switch
                  checked={formData.is_truck_driver}
                  onCheckedChange={(v) => setFormData({ ...formData, is_truck_driver: v })}
                  data-testid="employee-truck-driver-switch"
                />
              </div>
              <DialogFooter>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  data-testid="save-employee-btn"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white w-full"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    editingEmployee ? 'Update Employee' : 'Create Employee'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              placeholder="Search by name, email, or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="employee-search-input"
              className="pl-10 bg-stone-50 border-stone-200"
            />
          </div>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Users className="w-5 h-5 text-stone-400" />
            Team Members ({filteredEmployees.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No employees found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-stone-100">
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Name</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Email</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Department</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Hourly Rate</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Role</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map((employee) => (
                    <TableRow key={employee.id} className="border-stone-100 hover:bg-stone-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {employee.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </div>
                          <span className="font-medium">{employee.full_name}</span>
                          {employee.is_truck_driver && <Badge className="bg-sky-100 text-sky-700" data-testid={`truck-driver-badge-${employee.id}`}>Truck Driver</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-stone-600">{employee.email}</TableCell>
                      <TableCell className="text-stone-600">
                        {employee.department || '-'}
                      </TableCell>
                      <TableCell className="text-emerald-600 font-mono font-medium">${(employee.hourly_rate || 0).toFixed(2)}/hr</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          employee.role === 'admin' 
                            ? 'bg-purple-100 text-purple-800 border-purple-200' 
                            : 'bg-blue-100 text-blue-800 border-blue-200'
                        }>
                          {employee.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          employee.is_active 
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200' 
                            : 'bg-stone-100 text-stone-600 border-stone-200'
                        }>
                          {employee.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(employee)}
                            data-testid={`edit-employee-${employee.id}`}
                            className="hover:bg-emerald-50"
                          >
                            <Pencil className="w-4 h-4 text-stone-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleActive(employee)}
                            data-testid={`toggle-employee-${employee.id}`}
                            className="hover:bg-amber-50"
                          >
                            {employee.is_active ? (
                              <UserX className="w-4 h-4 text-amber-600" />
                            ) : (
                              <UserCheck className="w-4 h-4 text-emerald-600" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(employee)}
                            data-testid={`delete-employee-${employee.id}`}
                            className="hover:bg-rose-50"
                          >
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
